import { useEffect, useState } from "react";
import { Link, useParams } from "react-router-dom";
import { ArrowLeft, Stamp, Sparkles } from "lucide-react";
import Marquee from "react-fast-marquee";
import { api } from "@/lib/api";
import { detectLane, LANE_THEMES, COLLECTION_BANNERS } from "@/lib/laneTheme";
import SEO, { breadcrumbSchema } from "@/components/SEO";
import {
  SquigglyUnderline,
  BadFrog,
  StarBlob,
  UpsettingDog,
  Scribble,
  HandArrow,
} from "@/components/Doodle";

export default function Collection() {
  const { handle } = useParams();
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [notFound, setNotFound] = useState(false);

  useEffect(() => {
    setLoading(true);
    setNotFound(false);
    api
      .get(`/collections/${handle}`)
      .then((r) => setData(r.data))
      .catch((err) => {
        if (err?.response?.status === 404) setNotFound(true);
      })
      .finally(() => setLoading(false));
  }, [handle]);

  if (notFound) {
    return (
      <div className="mx-auto max-w-[800px] px-6 py-32 text-center md:px-10">
        <h1 className="font-display text-5xl font-black">Collection not found.</h1>
        <p className="mt-4">It got too damp. It dissolved.</p>
        <Link to="/shop" className="wc-btn mt-8">
          Back to shop
        </Link>
      </div>
    );
  }

  const laneKey = data ? detectLane(data.products) : null;
  const theme = laneKey ? LANE_THEMES[laneKey] : null;
  const isDamp = laneKey === "damp";
  const isJokes = laneKey === "jokes";
  const isCrimes = laneKey === "crimes";

  // Banner: prefer the per-collection witty copy. Fall back to the lane copy.
  // If the collection is empty (no lane detected) but has a custom banner,
  // render it with the jokes theme as a neutral fallback so the page still
  // has personality.
  const customBannerCopy = COLLECTION_BANNERS[handle];
  const bannerTheme = theme || (customBannerCopy ? LANE_THEMES.jokes : null);
  const bannerCopy = customBannerCopy || theme?.bannerCopy;
  const bannerSymbol = isCrimes || isDamp ? "✦" : "★";

  const pageBg = theme?.page || "bg-[#F9F9F4]";
  const heroBg = theme ? "" : "bg-[#EBE7DD]";
  const heroText = theme?.heroText || "text-[#0F0F0F]";
  const linkColor = isCrimes || isDamp ? "text-[#93C065] hover:text-[#F9F9F4]" : "text-[#0F0F0F]";

  return (
    <div className={`min-h-screen ${pageBg}`} data-testid="collection-page">
      <SEO
        title={data?.title || "Collection"}
        path={`/collections/${handle}`}
        description={
          (data?.description || "").slice(0, 160) ||
          `Browse the ${data?.title || "Wet Cabbage"} collection — premium streetwear documenting modern leisure behaviour.`
        }
        image={data?.products?.[0]?.image}
        type="website"
        schema={
          data
            ? breadcrumbSchema([
                { name: "Shop", path: "/shop" },
                { name: data.title, path: `/collections/${handle}` },
              ])
            : null
        }
      />

      {/* HERO — themed per lane */}
      <section
        className={`relative overflow-hidden border-b-2 border-[#1A3626] py-16 md:py-24 ${heroBg} ${heroText}`}
      >
        <div className="pointer-events-none absolute inset-0">
          {!theme && (
            <>
              <BadFrog className="absolute right-[4%] top-[12%] hidden h-24 w-24 -rotate-[12deg] md:block md:h-36 md:w-36" />
              <StarBlob className="absolute left-[8%] bottom-12 hidden h-12 w-12 rotate-[14deg] md:block" />
            </>
          )}
          {isJokes && (
            <>
              <UpsettingDog className="absolute -bottom-2 right-[6%] hidden h-32 w-32 -rotate-[10deg] md:block lg:right-[10%] lg:h-44 lg:w-44" />
              <Scribble className="absolute right-[14%] top-[14%] hidden h-8 w-24 -rotate-[8deg] opacity-70 md:block" />
              <StarBlob className="absolute left-[2%] top-[36%] hidden h-12 w-12 rotate-[14deg] opacity-80 md:block lg:h-16 lg:w-16" />
            </>
          )}
          {isCrimes && (
            <>
              <BadFrog className="absolute right-[6%] top-1/2 hidden h-24 w-24 -translate-y-1/2 rotate-[8deg] md:block lg:right-[10%] lg:h-32 lg:w-32" />
              <HandArrow className="absolute left-[3%] top-[40%] hidden h-10 w-24 rotate-[20deg] md:block" />
              <div
                className="absolute right-[14%] top-[12%] hidden -rotate-[12deg] border-4 border-[#93C065] px-5 py-2 font-display text-xs font-black uppercase tracking-[0.3em] text-[#93C065] md:block"
                aria-hidden
              >
                ✦ Designed For Modern Leisure ✦
              </div>
            </>
          )}
        </div>

        <div className="relative mx-auto max-w-[1100px] px-6 md:px-10">
          <Link
            to="/shop"
            className={`font-display inline-flex items-center gap-2 text-xs font-bold uppercase tracking-widest hover:underline ${linkColor}`}
            data-testid="collection-back-shop"
          >
            <ArrowLeft className="h-4 w-4" strokeWidth={2.5} /> All collections
          </Link>

          <div
            className={`font-display mt-6 text-xs font-bold uppercase tracking-[0.3em] ${theme?.kickerColor || ""}`}
          >
            {theme ? theme.kicker : "Collection · ongoing"}
          </div>

          {loading || !data ? (
            <div className="mt-3 h-20 w-2/3 animate-pulse bg-[#F9F9F4]/30" />
          ) : (
            <h1
              className="font-display mt-3 text-5xl font-black leading-[0.9] tracking-tighter sm:text-7xl lg:text-[112px]"
              data-testid="collection-title"
            >
              {data.title}.
            </h1>
          )}

          <div className="mt-3 max-w-md">
            <SquigglyUnderline
              className="h-4 w-full"
              stroke={theme?.underline || "#93C065"}
            />
          </div>

          {data?.description && (
            <p
              className="mt-8 max-w-3xl text-base leading-relaxed md:text-lg"
              data-testid="collection-description"
            >
              {data.description}
            </p>
          )}

          {data && (
            <div
              className={`mt-8 inline-flex items-center gap-3 border-2 px-4 py-2 ${
                isCrimes ? "border-[#F9F9F4] bg-transparent" : "border-[#1A3626] bg-[#F9F9F4]"
              }`}
            >
              <span className="font-display text-xs font-bold uppercase tracking-[0.3em]">
                {data.products.length} {data.products.length === 1 ? "piece" : "pieces"}
              </span>
            </div>
          )}
        </div>
      </section>

      {/* LANE-SPECIFIC TICKER BANNER */}
      {bannerTheme && bannerCopy && (
        <section
          className={`border-b-2 border-[#1A3626] py-3 ${bannerTheme.bannerBg}`}
          data-testid={`collection-banner-${handle}`}
        >
          <Marquee gradient={false} speed={70} pauseOnHover>
            {Array.from({ length: 4 }).map((_, i) => (
              <span
                key={i}
                className={`font-display mx-8 text-lg font-black uppercase tracking-tight md:text-2xl ${bannerTheme.bannerText}`}
              >
                {bannerSymbol} {bannerCopy}
              </span>
            ))}
          </Marquee>
        </section>
      )}

      {/* PRODUCT GRID */}
      <section className="py-16 md:py-24">
        <div className="mx-auto max-w-[1400px] px-6 md:px-10">
          {loading ? (
            <div className="grid grid-cols-1 gap-6 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4">
              {Array.from({ length: 6 }).map((_, i) => (
                <div
                  key={i}
                  className={`aspect-[4/5] animate-pulse border-2 ${
                    isCrimes ? "border-[#F9F9F4]" : "border-[#1A3626]"
                  } bg-[#EBE7DD]`}
                />
              ))}
            </div>
          ) : !data || data.products.length === 0 ? (
            <div
              className={`border-2 border-dashed p-16 text-center font-display font-bold uppercase tracking-wide ${
                isCrimes ? "border-[#F9F9F4] text-[#F9F9F4]" : "border-[#1A3626]"
              }`}
              data-testid="collection-empty"
            >
              Nothing in this collection (yet).
            </div>
          ) : (
            <div
              className="grid grid-cols-1 gap-6 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4"
              data-testid="collection-grid"
            >
              {data.products.map((p, i) => (
                <CollectionCard
                  key={p.id}
                  product={p}
                  index={i}
                  theme={theme}
                  laneKey={laneKey}
                />
              ))}
            </div>
          )}
        </div>
      </section>
    </div>
  );
}

function CollectionCard({ product, index, theme, laneKey }) {
  const cardBg = theme?.cardBg || "bg-[#EBE7DD]";
  const cardBorder = theme?.cardBorder || "border-[#1A3626]";
  const priceBg = theme?.priceBg || "bg-[#93C065]";
  const priceColor = theme?.priceColor || "text-[#0F0F0F]";
  const priceShape = theme?.priceShape || "";
  const priceRotate = theme?.priceRotate || "-rotate-3";
  const titleBg = theme?.titleBg || "bg-gradient-to-t from-[#0F0F0F] via-[#0F0F0F]/70 to-transparent";

  return (
    <Link
      to={`/shop/${product.id}`}
      className={`wc-card group relative block overflow-hidden wc-fade-up border-2 ${cardBorder} ${cardBg}`}
      style={{ animationDelay: `${index * 40}ms` }}
      data-testid={`collection-card-${product.id}`}
    >
      <div className="relative aspect-[4/5] overflow-hidden">
        {product.image ? (
          <img
            src={product.image}
            alt={product.name}
            className="h-full w-full object-cover transition-transform duration-700 group-hover:scale-105"
            loading="lazy"
          />
        ) : (
          <div className="flex h-full w-full items-center justify-center text-[#1A3626] opacity-40">
            no image yet
          </div>
        )}

        <div
          className={`font-display absolute right-3 top-3 ${priceRotate} border-2 border-[#1A3626] ${priceBg} ${priceColor} ${priceShape} px-3 py-1 text-xs font-black tracking-widest`}
        >
          {product.currency === "USD" ? "$" : ""}
          {product.price.toFixed(0)}
          {product.currency && product.currency !== "USD" ? ` ${product.currency}` : ""}
        </div>

        {laneKey === "damp" && (
          <div
            className="font-display absolute left-3 top-3 inline-flex items-center gap-1 border-2 border-[#1A3626] bg-[#93C065] px-2.5 py-1 text-[10px] font-black uppercase tracking-widest text-[#0F0F0F]"
            aria-hidden
          >
            <Sparkles className="h-3 w-3" strokeWidth={3} />
            Main Line
          </div>
        )}
        {laneKey === "jokes" && (
          <div
            className="font-display absolute left-3 top-3 inline-flex items-center gap-1 rounded-full border-2 border-[#1A3626] bg-[#F9F9F4] px-2.5 py-1 text-[10px] font-black uppercase tracking-widest text-[#0F0F0F]"
            aria-hidden
          >
            <Sparkles className="h-3 w-3" strokeWidth={3} />
            LOL
          </div>
        )}
        {laneKey === "crimes" && (
          <div
            className="font-display absolute left-3 top-3 inline-flex -rotate-[6deg] items-center gap-1 border-2 border-[#0F0F0F] bg-[#93C065] px-2 py-1 text-[10px] font-black uppercase tracking-[0.2em] text-[#0F0F0F]"
            aria-hidden
          >
            <Stamp className="h-3 w-3" strokeWidth={3} />
            Exhibit · {String(index + 1).padStart(2, "0")}
          </div>
        )}

        <div className={`absolute inset-x-0 bottom-0 ${titleBg} p-4 pt-10`}>
          <div className="font-display line-clamp-2 text-sm font-bold uppercase leading-tight tracking-tight text-[#F9F9F4]">
            {product.name}
          </div>
        </div>
      </div>
    </Link>
  );
}
